import {
  Flex,
  Heading,
  Text,
  useColorModeValue,
  Avatar,
  Stack,
  Icon,
  Tag,
  IconButton,
  Tooltip,
  Box,
  Input,
  Button,
  FormControl,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Textarea,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  FormLabel,
  HStack,
  Wrap,
  WrapItem,
  TagLabel,
  TagCloseButton,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import {
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
} from 'react-icons/fi';
import '../../theme/solarizedTheme';
import { Loading } from '../../helpers/Loading';
import { customStyles } from '../../helpers/customStyles';
import DownloadStudentReport from './StudentReport';
import { getMateria, updateMateria } from '../../features/materiaSlice';
import { getAllMatriculasByCurso, reset } from '../../features/matriculaSlice';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { FaFileExcel, FaGraduationCap } from 'react-icons/fa';
import { getAllConfiguraciones } from '../../features/configuracionSlice';

import CustomBackRoute from '../../helpers/CustomBackRoute';
import { AddIcon } from '@chakra-ui/icons';
import Select from 'react-select';
import { getActiveAcademicYear } from '../../features/academicYearSlice';
import { saveAllCalificaciones, getAllNotasPorMateria } from '../../features/notaSlice';


const DetalleMateria = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const toast = useToast();

  const themeTable = useColorModeValue('default', 'solarized');

  const { materia } = useSelector(state => state.materias);
  const { notasByMateria, isLoading, isLoadingSave } = useSelector(state => state.calificaciones);

  const { active_academic_year } = useSelector(state => state.academic_year);
  const { user } = useSelector(state => state.auth);
  const { configuracion } = useSelector(state => state.configuraciones);

  // Esta pantalla registra notas: se rige por el permiso de registro,
  // no el de modificacion (ese aplica a la edicion de notas ya publicadas).
  const registroDeNotasDeshabilitado = configuracion?.permitirRegistrarNotas === false;

  const [search, setSearch] = useState('');
  const [notasData, setNotasData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Función para obtener la nota del periodo actual
  const getNotaDelPeriodo = (row, periodo) => {
    const periodoNum = Number(periodo);
    switch (periodoNum) {
      case 1: return row.bimestre1;
      case 2: return row.bimestre2;
      case 3: return row.bimestre3;
      case 4: return row.bimestre4;
      default: return null;
    }
  };

  // Función para resetear todo el estado
  const resetearEstado = () => {
    setNotasData({});
    setHasChanges(false);
    setValidationErrors({});
    setSearch('');
  };

  // Cargar datos cuando cambia la materia
  useEffect(() => {
    const loadData = async () => {
      resetearEstado();

      await dispatch(getMateria(params.id));
      await dispatch(getAllMatriculasByCurso(params.id));
      await dispatch(getAllConfiguraciones());
      await dispatch(getActiveAcademicYear());
    };

    loadData();

    return () => {
      dispatch(reset());
      resetearEstado();
    };
  }, [dispatch, params.id]);

  // Cargar notas cuando están disponibles materia y año académico
  useEffect(() => {
    if (materia?._id && materia?.grado?._id) {
      dispatch(getAllNotasPorMateria({
        materia: materia._id,
        grado: materia.grado._id
      }));
    }
  }, [dispatch, materia?._id, materia?.grado?._id]);

  // Inicializar notasData cuando llegan los datos
  useEffect(() => {
    if (notasByMateria && notasByMateria.length > 0 && active_academic_year?.periodo) {
      const periodo = Number(active_academic_year.periodo);
      const initialData = {};

      notasByMateria.forEach(row => {
        const notaActual = getNotaDelPeriodo(row, periodo);

        initialData[row.matriculaId] = {
          matriculaId: row.matriculaId,
          nota: notaActual || '',
          fallas: row.fallas || 0,
          recuperacion: row.recuperacion || '',
          observaciones: row.observaciones || '',
          indicadores: row.indicadores || [],
          valoraciones: row.valoraciones || []
        };
      });

      setNotasData(initialData);
    } else {
      setNotasData({});
    }
  }, [notasByMateria, active_academic_year?.periodo]);

  const validateData = () => {
    const errors = {};
    let hasErrors = false;

    Object.entries(notasData).forEach(([key, values]) => {
      const studentErrors = {};

      if (values.nota && (isNaN(values.nota) || values.nota < 0 || values.nota > 5)) {
        studentErrors.nota = 'La nota debe ser un número entre 0 y 5';
        hasErrors = true;
      }

      if (values.fallas && (isNaN(values.fallas) || values.fallas < 0)) {
        studentErrors.fallas = 'Las fallas deben ser un número positivo';
        hasErrors = true;
      }

      if (Object.keys(studentErrors).length > 0) {
        errors[key] = studentErrors;
      }
    });

    setValidationErrors(errors);
    return !hasErrors;
  };

  const saveAll = async () => {
    if (!validateData()) {
      toast({
        title: 'Error de validación',
        description: 'Por favor corrige los errores antes de guardar',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const periodo = Number(active_academic_year?.periodo);

    const changedData = Object.entries(notasData).filter(([key, values]) => {
      const originalRow = notasByMateria.find(row => row.matriculaId === key);
      if (!originalRow) return true;

      const originalNota = getNotaDelPeriodo(originalRow, periodo);

      return (
        values.nota !== originalNota ||
        values.fallas !== (originalRow.fallas || 0) ||
        values.recuperacion !== (originalRow.recuperacion || '') ||
        values.observaciones !== (originalRow.observaciones || '') ||
        JSON.stringify(values.indicadores) !== JSON.stringify(originalRow.indicadores || []) ||
        JSON.stringify(values.valoraciones) !== JSON.stringify(originalRow.valoraciones || [])
      );
    });

    if (changedData.length === 0) {
      toast({
        title: 'Sin cambios',
        description: 'No hay modificaciones que guardar',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const data = changedData.map(([key, values]) => {
      const notaObj = {
        matricula: values.matriculaId || key,
        materia: params.id,
        profesor: user?.usuario?.id || null,
        fallas: parseInt(values.fallas) || 0,
        recuperacion: values.recuperacion ? parseFloat(values.recuperacion) : null,
        observaciones: values.observaciones || "",
        estado: values.nota ? "Calificado" : "Pendiente",
      };

      // Asignar nota al bimestre correspondiente
      if (periodo === 1) notaObj.bimestre1 = parseFloat(values.nota) || 0;
      if (periodo === 2) notaObj.bimestre2 = parseFloat(values.nota) || 0;
      if (periodo === 3) notaObj.bimestre3 = parseFloat(values.nota) || 0;
      if (periodo === 4) notaObj.bimestre4 = parseFloat(values.nota) || 0;

      if (values.indicadores && values.indicadores.length > 0) {
        notaObj.indicadores = values.indicadores;
      }

      if (values.valoraciones && values.valoraciones.length > 0) {
        notaObj.valoraciones = values.valoraciones;
      }

      return notaObj;
    });

    try {
      const result = await dispatch(saveAllCalificaciones({ notas: data })).unwrap();

      if (result.errores && result.errores.length > 0) {
        toast({
          title: 'Advertencias',
          description: `${result.errores.length} registros con errores`,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Éxito',
          description: 'Calificaciones guardadas correctamente',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      setHasChanges(false);
      dispatch(getAllNotasPorMateria({ materia: params.id, grado: materia?.grado?._id }));

    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Error al guardar las calificaciones',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const updateNotaData = (matriculaId, field, value) => {
    setNotasData((prev) => ({
      ...prev,
      [matriculaId]: {
        ...prev[matriculaId],
        matriculaId: matriculaId,
        [field]: value
      }
    }));
    setHasChanges(true);

    if (validationErrors[matriculaId]?.[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[matriculaId][field];
        if (Object.keys(newErrors[matriculaId]).length === 0) {
          delete newErrors[matriculaId];
        }
        return newErrors;
      });
    }
  };

  const clearAllChanges = () => {
    // Recargar datos originales
    if (notasByMateria && notasByMateria.length > 0 && active_academic_year?.periodo) {
      const periodo = Number(active_academic_year.periodo);
      const initialData = {};

      notasByMateria.forEach(row => {
        const notaActual = getNotaDelPeriodo(row, periodo);

        initialData[row.matriculaId] = {
          matriculaId: row.matriculaId,
          nota: notaActual || '',
          fallas: row.fallas || 0,
          recuperacion: row.recuperacion || '',
          observaciones: row.observaciones || '',
          indicadores: row.indicadores || [],
          valoraciones: row.valoraciones || []
        };
      });

      setNotasData(initialData);
    }

    setHasChanges(false);
    setValidationErrors({});

    toast({
      title: 'Cambios borrados',
      description: 'Se han eliminado todos los cambios pendientes',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  const filteredData = notasByMateria.filter(row =>
    (row.estudiante?.nombres + ' ' + row.estudiante?.apellidos)
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const exportToExcel = () => {
    const periodo = Number(active_academic_year?.periodo);

    // La nota vive en bimestre1..4 segun el periodo activo. notasData refleja
    // ademas lo que el docente tiene escrito en pantalla sin guardar todavia.
    const notaDeLaFila = row => {
      const enPantalla = notasData[row.matriculaId]?.nota;
      const guardada = getNotaDelPeriodo(row, periodo);
      const valor =
        enPantalla !== '' && enPantalla !== undefined && enPantalla !== null
          ? enPantalla
          : guardada;

      return valor === '' || valor === undefined || valor === null
        ? 'N/A'
        : valor;
    };

    const worksheet = XLSX.utils.json_to_sheet(
      notasByMateria.map((row, index) => ({
        N: index + 1,
        Estudiante: row.estudiante?.apellidos + ' ' + row.estudiante?.nombres,
        Grado: materia?.grado?.nombre || 'N/A',
        Anio: active_academic_year?.year || 'N/A',
        Periodo: periodo || 'N/A',
        Nota: notaDeLaFila(row),
        Fallas: notasData[row.matriculaId]?.fallas ?? row.fallas ?? 0,
        Estado: row.estado || 'N/A',
        Promedio: row.promedio ? row.promedio.toFixed(2) : 'N/A',
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Estudiantes');
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(data, `${materia.nombre}_${active_academic_year?.year}_P${active_academic_year?.periodo}.xlsx`);
  };

  const columns = [
    {
      name: 'N',
      selector: row => notasByMateria?.indexOf(row) + 1,
      sortable: true,
      width: '70px',
    },
    {
      name: 'ESTUDIANTE',
      selector: row => row.estudiante?.apellidos + ' ' + row.estudiante?.nombres,
      sortable: true,
      cellExport: row =>
        row.estudiante?.apellidos + ' ' + row.estudiante?.nombres,
      resizable: true,
      cell: row => (
        <Stack spacing={2} direction="row">
          <Avatar
            size="sm"
            name={row.estudiante?.apellidos + ' ' + row.estudiante?.nombres}
            src={row?.img}
            fontWeight="bold"
            fontSize="sm"
            color="white"
            display={{ base: 'none', sm: 'none', lg: 'flex' }}
          />
          <Text ml={2} fontSize="12px" alignSelf={'center'}>
            {row.estudiante?.apellidos + ' ' + row.estudiante?.nombres}
          </Text>
        </Stack>
      ),
      minWidth: '220px',
      wrap: true,
    },
    {
      name: 'NOTA',
      selector: row => notasData[row.matriculaId]?.nota || '',
      sortable: true,
      resizable: true,
      center: true,
      cell: row => {
        const hasError = validationErrors[row.matriculaId]?.nota;
        const currentValue = notasData[row.matriculaId]?.nota ?? '';

        return (
          <FormControl maxW="80px" isInvalid={hasError}>
            <Input
              type="number"
              step="0.01"
              min="0"
              max="5"
              value={currentValue}
              placeholder="0.0"
              size="md"
              fontSize="xs"
              textAlign="center"
              borderColor={hasError ? 'red.300' : undefined}
              isDisabled={registroDeNotasDeshabilitado}
              onChange={e => {
                updateNotaData(row.matriculaId, 'nota', e.target.value);
              }}
            />
          </FormControl>
        );
      },
      width: '110px',
    },
    {
      name: 'INDICADOR',
      cell: row => (
        <ModalIndicadores
          toast={toast}
          materia={materia}
          rowId={row.matriculaId}
          setNotasData={setNotasData}
          notasData={notasData}
          setHasChanges={setHasChanges}
          key={row.matriculaId}
        />
      ),
      resizable: true,
      center: true,
      width: '120px',
    },
    {
      name: 'FALLAS',
      selector: row => notasData[row.matriculaId]?.fallas || 0,
      sortable: true,
      cellExport: row => row.fallas?.length || 0,
      resizable: true,
      center: true,
      cell: row => {
        const hasError = validationErrors[row.matriculaId]?.fallas;
        const currentValue = notasData[row.matriculaId]?.fallas ?? 0;
        return (
          <FormControl maxW="80px" isInvalid={hasError}>
            <Input
              type="number"
              placeholder="0"
              value={currentValue}
              min={0}
              max={100}
              size="md"
              textAlign="center"
              borderColor={hasError ? 'red.300' : undefined}
              onChange={e => {
                updateNotaData(row.matriculaId, 'fallas', e.target.value);
              }}
            />
          </FormControl>
        );
      },
      width: '110px',
    },
    {
      name: 'OBS.',
      cell: row => (
        <ModalAddObservaciones
          data={row}
          setNotasData={setNotasData}
          setHasChanges={setHasChanges}
          notasData={notasData}
        />
      ),
      resizable: true,
      center: true,
      width: '110px',
    },
    {
      name: 'ESTADO',
      selector: row => row.estado,
      sortable: true,
      cell: row => (
        <Tag
          size="md"
          variant="solid"
          colorScheme={
            row.estado === 'Calificado'
              ? 'green'
              : row.estado === 'Aprobado'
                ? 'blue'
                : row.estado === 'Reprobado'
                  ? 'red'
                  : 'gray'
          }
          textAlign="center"
        >
          {row.estado || 'N/A'}
        </Tag>
      ),
      cellExport: row => row.estado,
      resizable: true,
      center: true,
      minWidth: '150px',
    },
    {
      name: 'REC.',
      selector: row => notasData[row.matriculaId]?.recuperacion || '',
      sortable: true,
      cellExport: row =>
        row.recuperacion ? row.recuperacion.toFixed(2) : 'N/A',
      resizable: true,
      center: true,
      cell: row => {
        const todasNotasRegistradas =
          row.bimestre1 != null &&
          row.bimestre1 !== 0 &&
          row.bimestre2 != null &&
          row.bimestre2 !== 0 &&
          row.bimestre3 != null &&
          row.bimestre3 !== 0 &&
          row.bimestre4 != null &&
          row.bimestre4 !== 0;

        const currentValue = notasData[row.matriculaId]?.recuperacion ?? '';

        return (
          <Input
            type="number"
            step="0.01"
            min="0"
            max="5"
            value={currentValue}
            placeholder="0.0"
            onChange={e => {
              updateNotaData(row.matriculaId, 'recuperacion', e.target.value);
            }}
            size="md"
            textAlign="center"
            isDisabled={!(todasNotasRegistradas && row.promedio < 3.0)}
            bg="gray.100"
            _dark={{ bg: 'gray.700' }}
          />
        );
      },
      width: '110px',
    },
    {
      name: 'PROM.',
      selector: row => (row.promedio ? row.promedio.toFixed(2) : 'N/A'),
      sortable: true,
      cellExport: row => (row.promedio ? row.promedio.toFixed(2) : 'N/A'),
      resizable: true,
      center: true,
      width: '110px',
    },
    // {
    //   name: 'ACCIONES',
    //   export: false,
    //   center: true,
    //   cell: row => (
    //     <Stack direction="row" spacing={2}>
    //       {row.grado?.nivel === 'INICIAL' ? (
    //         <Link
    //           to={`/mis-asignaturas/${row.matriculaId}/registrar-calificacion-inicial/${materia?._id}`}
    //         >
    //           <IconButton
    //             aria-label="Editar calificación inicial"
    //             icon={<Check size={24} />}
    //             isDisabled={!configuracion?.permitirRegistrarNotas}
    //             colorScheme="green"
    //             isRound
    //             variant="solid"
    //           />
    //         </Link>
    //       ) : null}
    //       <ModalVerTodasCalificaciones
    //         estudiante={row.estudiante}
    //         curso={materia}
    //         notas={row}
    //       />
    //     </Stack>
    //   ),
    // },
  ];

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div>
      <Box
        borderTop="8px solid"
        borderColor={materia?.brand_color || 'primary.500'}
        borderRadius="2xl"
        bg={'white'}
        boxShadow={'base'}
        _dark={{
          bg: 'primary.1000',
          color: 'white',
        }}
        p={6}
      >
        <Flex justify="space-between" align="center">
          <CustomBackRoute />
          <Stack
            spacing={2}
            ml={2}
            w="full"
            direction="row"
            justifyContent={'space-between'}
            alignSelf={'center'}
          >
            <Heading size="md" alignSelf={'center'}>
              {materia?.nombre}
            </Heading>
            <Stack direction="row" spacing={2}>
              <ModalGestionarIndicadores materia={materia} />
              <Tag colorScheme={'purple'} px={4} variant={'outline'}>
                <Icon as={FaGraduationCap} mr={2} boxSize={4} />
                GRADO: {materia?.grado?.nombre}
              </Tag>
            </Stack>
          </Stack>
        </Flex>

        <Stack
          direction={['column', 'column', 'row']}
          justifyContent={'space-between'}
          spacing={4}
          py={2}
        >
          <Input
            placeholder="Buscar estudiante..."
            variant={'filled'}
            size="lg"
            maxW={'100%'}
            focusBorderColor="primary.100"
            colorScheme="primary"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <Stack direction={'row'} spacing={2}>
            <Tooltip label={'Exportar a Excel'} placement="auto">
              <IconButton
                aria-label="Exportar a Excel"
                icon={<FaFileExcel size={24} />}
                size="lg"
                colorScheme="green"
                variant="solid"
                onClick={exportToExcel}
              />
            </Tooltip>
            <DownloadStudentReport id={params.id} />
          </Stack>
        </Stack>

        {hasChanges && (
          <Alert status="info" mb={4} borderRadius="md">
            <AlertIcon />
            <AlertTitle>Cambios pendientes</AlertTitle>
            <AlertDescription>
              Tienes modificaciones sin guardar. No olvides hacer clic en "GUARDAR REGISTROS".
            </AlertDescription>
          </Alert>
        )}

        {Object.keys(validationErrors).length > 0 && (
          <Alert status="error" mb={4} borderRadius="md">
            <AlertIcon />
            <AlertTitle>Errores de validación</AlertTitle>
            <AlertDescription>
              Hay {Object.keys(validationErrors).length} estudiante(s) con datos inválidos.
            </AlertDescription>
          </Alert>
        )}

        <Stack mb={4} direction={['column', 'column', 'row']} justify="space-between" align="center">
          <Heading alignSelf={'center'} fontWeight={'normal'} size="sm">
            CALIFICACIÓN DEL PERIODO: <span style={{ fontWeight: 'bold' }}>
              {active_academic_year?.year} - P{active_academic_year?.periodo}
            </span>
          </Heading>
          <Stack direction="row" justify={'flex-end'} spacing={1}>
            <Button
              variant="solid"
              size={'sm'}
              colorScheme="red"
              onClick={clearAllChanges}
              isDisabled={!hasChanges || registroDeNotasDeshabilitado}
            >
              BORRAR CAMBIOS
            </Button>
            <Button
              colorScheme="primary"
              size={'sm'}
              variant="solid"
              onClick={saveAll}
              isLoading={isLoadingSave}
              loadingText="Guardando..."
              isDisabled={
                !hasChanges ||
                Object.keys(validationErrors).length > 0 ||
                registroDeNotasDeshabilitado
              }
            >
              GUARDAR REGISTROS
            </Button>
          </Stack>
        </Stack>

        <DataTable
          columns={columns}
          data={filteredData}
          defaultSortField="createdAt"
          defaultSortAsc={true}
          defaultSortOrder="desc"
          pagination={true}
          paginationIconFirstPage={
            <Icon
              as={FiChevronsLeft}
              boxSize={6}
              _dark={{ color: 'gray.300' }}
            />
          }
          paginationIconLastPage={
            <Icon
              as={FiChevronsRight}
              boxSize={6}
              _dark={{ color: 'gray.300' }}
            />
          }
          paginationIconPrevious={
            <Icon
              as={FiChevronLeft}
              boxSize={6}
              _dark={{
                color: 'gray.300',
                _hover: { color: 'white' },
              }}
            />
          }
          paginationIconNext={
            <Icon
              as={FiChevronRight}
              boxSize={6}
              _dark={{
                color: 'gray.300',
                _hover: { color: 'white' },
              }}
            />
          }
          paginationRowsPerPageOptions={[100, 5, 10, 25, 50]}
          paginationPerPage={100}
          paginationComponentOptions={{
            rowsPerPageText: 'Filas por pagina:',
            rangeSeparatorText: 'de',
            noRowsPerPage: false,
            selectAllRowsItem: true,
            selectAllRowsItemText: 'Todos',
          }}
          theme={themeTable}
          customStyles={customStyles}
          responsive={true}
          noDataComponent={
            <Text mb={4} fontSize="lg">
              NO HAY REGISTROS
            </Text>
          }
        />
      </Box>
    </div>
  );
};

export default DetalleMateria;

function ModalIndicadores({ toast, materia, rowId, setNotasData, notasData, setHasChanges }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedIndicadores, setSelectedIndicadores] = useState([]);
  const { active_academic_year } = useSelector(state => state.academic_year);

  // Obtener el periodo actual
  const currentPeriodo = Number(active_academic_year?.periodo) || 1;

  useEffect(() => {
    if (isOpen && notasData[rowId]?.indicadores) {
      // Buscar los indicadores del periodo actual
      const indicadoresDelPeriodo = notasData[rowId].indicadores.find(
        (ind) => ind.periodo === currentPeriodo
      );

      if (indicadoresDelPeriodo && indicadoresDelPeriodo.indicador) {
        // Filtrar indicadores únicos del periodo actual
        const indicadoresUnicos = indicadoresDelPeriodo.indicador.reduce((acc, it) => {
          if (!acc.find(item => item.indicador === it.indicador)) {
            acc.push(it);
          }
          return acc;
        }, []);

        // Mapear los indicadores a opciones del Select
        const selectedOptions = indicadoresUnicos.map((it) => ({
          value: it.indicador,
          label: it.texto || materia?.indicadores?.find(
            ind => (ind.id || ind._id) === it.indicador
          )?.text || it.indicador
        }));

        setSelectedIndicadores(selectedOptions);
      } else {
        // Si no hay indicadores para este periodo, limpiar la selección
        setSelectedIndicadores([]);
      }
    }
  }, [isOpen, notasData, rowId, materia, currentPeriodo]);

  const handleIndicadorChange = (selected) => {
    if (selected && selected.length > 4) {
      toast({
        title: 'Límite de indicadores',
        description: 'Solo puedes seleccionar hasta 4 indicadores por estudiante.',
        status: 'warning',
        duration: 1000,
        isClosable: true,
      });
      return;
    }
    setSelectedIndicadores(selected || []);
  };

  const handleSaveIndicadores = () => {
    setNotasData((prev) => {
      const existingIndicadores = prev[rowId]?.indicadores || [];
      // Filtrar indicadores de otros periodos
      const otrosPeriodos = existingIndicadores.filter(ind => ind.periodo !== currentPeriodo);

      // Crear nuevo array de indicadores incluyendo los del periodo actual
      const newIndicadores = [
        ...otrosPeriodos,
        {
          periodo: currentPeriodo,
          indicador: selectedIndicadores.map((s) => ({
            indicador: s.value,
            texto: s.label
          }))
        }
      ];

      return {
        ...prev,
        [rowId]: {
          ...prev[rowId],
          matriculaId: rowId,
          indicadores: newIndicadores,
        },
      };
    });

    setHasChanges(true);
    onClose();
  };

  return (
    <>
      <Tooltip label={`Gestionar indicadores - Periodo ${currentPeriodo}`} placement="top">
        <IconButton
          icon={<AddIcon size={20} />}
          onClick={onOpen}
          colorScheme="primary"
          variant="solid"
          isRound
          size="sm"
        />
      </Tooltip>

      <Modal isOpen={isOpen} onClose={onClose} size={'6xl'} scrollBehavior="outside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Gestionar Indicadores - Periodo {currentPeriodo}
            <Text fontSize="sm" color="gray.500" fontWeight="normal">
              {materia?.nombre} | {materia?.grado?.nombre}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4} h={'400px'}>
              <Text fontSize="md" fontWeight="semibold">
                Selecciona los indicadores que aplican para este estudiante:
              </Text>
              {materia?.indicadores && materia.indicadores.length > 0 ? (
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  name="indicadores"
                  placeholder="Seleccione uno o más indicadores..."
                  options={materia.indicadores.map((it) => ({
                    value: it._id,
                    label: it.text,
                  }))}
                  value={selectedIndicadores}
                  onChange={handleIndicadorChange}
                  isClearable
                  isSearchable
                  menuPosition="fixed"
                  menuPlacement="auto"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: '50px',
                      borderRadius: 'md',
                    }),
                    multiValue: (provided) => ({
                      ...provided,
                      backgroundColor: '#38a169',
                      color: 'white',
                    }),
                    multiValueLabel: (provided) => ({
                      ...provided,
                      fontWeight: '500',
                      color: 'white',
                    }),
                  }}
                />
              ) : (
                <Alert status="warning">
                  <AlertIcon />
                  <AlertDescription>
                    No hay indicadores configurados para esta materia.
                    Contacta al administrador para configurar los indicadores.
                  </AlertDescription>
                </Alert>
              )}

              {selectedIndicadores.length > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>
                    Indicadores seleccionados ({selectedIndicadores.length}):
                  </Text>
                  <Text fontSize="sm" color="gray.500" mb={2}>
                    Puedes seleccionar hasta 4 indicadores ({selectedIndicadores.length}/4)
                  </Text>
                  <Stack spacing={2}>
                    {selectedIndicadores.map((indicador, index) => (
                      <Tag
                        key={index}
                        size="md"
                        variant="solid"
                        colorScheme="green"
                        px={3}
                        py={2}
                      >
                        {indicador.label}
                      </Tag>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="green"
              mr={3}
              onClick={handleSaveIndicadores}
              isDisabled={!materia?.indicadores || materia.indicadores.length === 0}
            >
              Guardar Cambios
            </Button>
            <Button onClick={onClose} variant="outline">
              Cancelar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

function ModalGestionarIndicadores({ materia }) {
  const dispatch = useDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [indicadores, setIndicadores] = useState([]);
  const [indicador, setIndicador] = useState({
    id: '',
    text: '',
  });

  useEffect(() => {
    if (isOpen) {
      if (materia?.indicadores && materia.indicadores.length > 0) {
        setIndicadores(materia.indicadores.map(ind => ({
          id: ind.id || Date.now() + Math.random(),
          text: ind.text
        })));
      } else {
        setIndicadores([]);
      }
      setIndicador({ id: '', text: '' });
    }
  }, [isOpen, materia]);

  const handleAddIndicador = () => {
    if (indicadores.length < 10 && indicador.text.trim() !== '') {
      setIndicadores([...indicadores, { id: Date.now(), text: indicador.text }]);
      setIndicador({ id: '', text: '' });
    }
  };

  const handleRemove = (id) => {
    const updatedIndicadores = indicadores.filter((it) => it.id !== id);
    setIndicadores(updatedIndicadores);
  };

  const handleSave = async () => {
    try {
      await dispatch(updateMateria({
        _id: materia._id,
        nombre: materia.nombre,
        indicadores: indicadores.map((ind, index) => ({
          id: index + 1,
          text: ind.text
        }))
      })).unwrap();

      toast({
        title: 'Indicadores actualizados',
        description: 'Los indicadores se han guardado correctamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Recargar la materia para obtener los datos actualizados
      await dispatch(getMateria(materia._id));
      onClose();
    } catch (error) {
      toast({
        title: 'Error al actualizar',
        description: error.message || 'No se pudieron guardar los indicadores',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Tooltip label="Gestionar indicadores de la materia" placement="top">
        <Button
          leftIcon={<AddIcon />}
          colorScheme="primary"
          variant="solid"
          size="sm"
          onClick={onOpen}
        >
          INDICADORES
        </Button>
      </Tooltip>

      <Modal isOpen={isOpen} onClose={onClose} size={'6xl'} scrollBehavior="inside">
        <ModalOverlay
          bg="rgba(11,15,25, 0.8)"
          backdropFilter="auto"
          backdropBlur="2px"
        />
        <ModalContent _dark={{ bg: 'primary.1000' }}>
          <ModalHeader>
            Gestionar Indicadores de {materia?.nombre}
            <Text fontSize="sm" color="gray.500" fontWeight="normal">
              {materia?.grado?.nombre}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl>
                <FormLabel fontWeight="semibold">
                  INDICADORES <Text as="span" fontWeight="normal">({indicadores.length}/10)</Text>
                </FormLabel>

                <HStack>
                  <Input
                    placeholder="Escribe un indicador"
                    type="text"
                    value={indicador.text}
                    onChange={(e) => setIndicador({ ...indicador, text: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddIndicador();
                      }
                    }}
                    maxLength={200}
                    aria-label="input-indicador"
                  />
                  <Button
                    colorScheme="primary"
                    onClick={handleAddIndicador}
                    isDisabled={indicadores.length >= 10}
                  >
                    Agregar
                  </Button>
                </HStack>
              </FormControl>

              {indicadores.length > 0 && (
                <Wrap>
                  {indicadores.map((it) => (
                    <WrapItem key={it.id}>
                      <Tag size="lg" borderRadius="full" variant="solid" colorScheme="primary">
                        <TagLabel>{it.text}</TagLabel>
                        <TagCloseButton onClick={() => handleRemove(it.id)} aria-label={`Eliminar ${it.text}`} />
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              )}

              {indicadores.length === 0 && (
                <Alert status="info">
                  <AlertIcon />
                  <AlertDescription>
                    No hay indicadores configurados. Agrega indicadores para que los docentes puedan asignarlos a los estudiantes.
                  </AlertDescription>
                </Alert>
              )}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="green"
              mr={3}
              onClick={handleSave}
              size="lg"
              borderRadius="xl"
            >
              GUARDAR CAMBIOS
            </Button>
            <Button onClick={onClose} variant="outline" size="lg" borderRadius="xl">
              CANCELAR
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

function ModalAddObservaciones({ data, setNotasData, setHasChanges, notasData }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [observacionText, setObservacionText] = useState('');

  useEffect(() => {
    if (isOpen) {
      const currentObservacion = notasData[data.matriculaId]?.observaciones || data.observaciones || '';
      setObservacionText(currentObservacion);
    }
  }, [isOpen, data.observaciones, data.matriculaId, notasData]);

  const handleObservacionChange = (value) => {
    setObservacionText(value);
  };

  const handleSaveObservacion = () => {
    setNotasData((prev) => ({
      ...prev,
      [data.matriculaId]: {
        ...prev[data.matriculaId],
        matriculaId: data.matriculaId,
        observaciones: observacionText.trim(),
      },
    }));
    setHasChanges(true);
    onClose();
  };

  const hasObservacion = (notasData[data.matriculaId]?.observaciones || data.observaciones || '').trim().length > 0;

  return (
    <>
      <Tooltip
        label={hasObservacion ? "Editar observaciones" : "Agregar observaciones"}
        placement="top"
      >
        <IconButton
          icon={<AddIcon size={20} />}
          onClick={onOpen}
          colorScheme={hasObservacion ? "blue" : "yellow"}
          variant={hasObservacion ? "solid" : "outline"}
          isRound
          size="sm"
        />
      </Tooltip>

      <Modal isOpen={isOpen} onClose={onClose} size={'4xl'} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {hasObservacion ? "Editar" : "Agregar"} Observaciones
            <Text fontSize="sm" color="gray.500" fontWeight="normal">
              {data.estudiante?.nombres} {data.estudiante?.apellidos}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <Stack spacing={4}>
              <Text fontSize="md" fontWeight="semibold">
                Observaciones sobre el desempeño del estudiante:
              </Text>

              <Textarea
                placeholder="Escribe aquí las observaciones sobre el comportamiento, participación, dificultades o logros del estudiante..."
                value={observacionText}
                onChange={(e) => handleObservacionChange(e.target.value)}
                size="lg"
                minH={'250px'}
                resize="vertical"
                focusBorderColor="blue.400"
                borderWidth={2}
              />

              <Box>
                <Text fontSize="xs" color="gray.500">
                  Caracteres: {observacionText.length} / 500
                </Text>
                {observacionText.length > 500 && (
                  <Text fontSize="xs" color="red.500">
                    Has excedido el límite recomendado de caracteres
                  </Text>
                )}
              </Box>

              {observacionText.trim().length > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>
                    Vista previa:
                  </Text>
                  <Box
                    p={3}
                    borderRadius="md"
                    bg="gray.50"
                    border="1px solid"
                    borderColor="gray.200"
                    _dark={{
                      bg: "gray.700",
                      borderColor: "gray.600",
                    }}
                  >
                    <Text fontSize="sm" whiteSpace="pre-wrap">
                      {observacionText}
                    </Text>
                  </Box>
                </Box>
              )}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="green"
              mr={3}
              onClick={handleSaveObservacion}
            >
              Guardar Observación
            </Button>
            <Button onClick={onClose} variant="outline">
              Cancelar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}